// Internal modules
import { appStore } from '@/store';
import { isDev } from '@/lib/utils/env';
import { logger } from '@/lib/logger';
import { getAccessToken } from '@/lib/globals';
import { getErrorCodeFromErrorData } from './utils';
import { CPA_AUTH_DECLINED_ERROR, SHARED_FI_ERROR } from './constants';
import {
  GENERIC_ERRORS,
  GUEST_CPA_ERRORS,
  PAGE_LEVEL_ERRORS,
  POST_ACCOUNT_CREATION_ERRORS,
  POST_ACCOUNT_CREATION_REBRANDED_ERRORS,
  PRE_ACCOUNT_CREATION_ERRORS,
} from './mappings';
import {
  handle3DSError,
  handleDefaultError,
  handleNamesValidationError,
  handlePageLevelError,
  handlePostAccountCreationError,
  isNameValidationError,
} from './handlers';
import type { errorType } from './types';
import { features } from '@/lib/features';
import { redirectToAuthChallenge, redirectToGenericError } from '@/lib/utils';
import { CI } from '../customerInteractions';
import {
  atomics,
  atomicsDomain,
  atomicsErrorContexts,
  atomicsErrorReason,
  atomicsStatus,
} from '@/lib/atomics';

const handleApiError = (error: errorType): void => {
  const { message: errorCode } = error;
  const isSharedFIEligible = features.isActive('isSharedFIEligible');
  const hasCheckoutVUIContigency = features.isActive(
    'hasCheckoutVUIContigency',
  );
  const isRebrandedErrorsEligible = features.isActive(
    'shouldShowRebrandedErrors',
  );

  /**
   * only show FI interstitial if unique FI error is thrown by xobuyer AND not in
   * SHIFT experiment
   */
  if (SHARED_FI_ERROR.hasOwnProperty(errorCode) && isSharedFIEligible) {
    appStore.getActions().ui.setIsFILimitExceededInterstitialOpen(true);
    CI.endWith(
      CI.Event.CREATE_ACCOUNT_AND_PAY_SUBMIT,
      CI.Status.Error,
      CI.ErrorReason.Flag,
    );
    atomics.markProblem({
      context: atomicsErrorContexts.fiLimitExceeded,
      domain: atomicsDomain,
      reason: atomicsErrorReason.flag,
      status: atomicsStatus.error,
    });
    if (appStore.getState().ui.fiType !== 'card') {
      CI.start(CI.Event.CONTINGENCY_BANK_ACCT_ALREADY_LINKED_USER_INPUT);
    }
    return;
  }

  if (errorCode === 'VERIFY_USER_IDENTITY') {
    logger.fpti('checkout_interrupted_by_tpv_error', {
      contingency: 'VERIFY_USER_IDENTITY',
      event_name: 'checkout_interrupted_by_tpv_error',
    });
  }

  if (errorCode === 'VERIFY_USER_IDENTITY' && hasCheckoutVUIContigency) {
    logger.fpti('weasley_verify_user_identity_treatment', {
      event_name: 'weasley_verify_user_identity_treatment',
    });
    logger.info('weasley_verify_user_identity_post_account_creation', {
      context: 'account_created_needs_verification',
      hasErrorData: !!error.errorData,
      hasStepUpContext: !!error.errorData?.step_up_context,
    });

    const { errorData } = error;
    const stepUpContext = errorData?.step_up_context;

    if (stepUpContext) {
      logger.info('weasley_verify_user_identity_invoking_auth_challenge', {
        flow: 'post_account_creation_identity_verification',
        hasStepUpContext: true,
        purpose: 'complete_account_verification',
      });
      // Dismiss the loading spinner before redirecting to Auth Node Web
      appStore.getActions().ui.toggleIsFormSubmitting(false);
      appStore.getActions().ui.toggleIsPageLoading(false);
      // Redirect to Auth Node Web for identity verification
      // After verification, user will return with verified account
      return redirectToAuthChallenge(stepUpContext);
    }

    logger.warn('weasley_verify_user_identity_missing_step_up_context', {
      error: errorCode,
      errorData,
      flow: 'post_account_creation',
    });

    // Fallback to default error handling if no step_up_context
    return handleDefaultError(error);
  }

  if (
    isRebrandedErrorsEligible &&
    Object.keys(POST_ACCOUNT_CREATION_REBRANDED_ERRORS).includes(errorCode)
  ) {
    appStore.getActions().ui.toggleIsPageLoading(true);

    return redirectToGenericError({
      message: POST_ACCOUNT_CREATION_REBRANDED_ERRORS[errorCode],
      params: { rebrandedErrors: true },
    });
  }

  if (errorCode === 'PAYER_LOGIN_COUNTRY_NOT_ALLOWED') {
    logger.fpti('checkout_interrupted_by_tpv_error', {
      contingency: 'PAYER_LOGIN_COUNTRY_NOT_ALLOWED',
      event_name: 'checkout_interrupted_by_tpv_error',
    });
  }

  // Check to see if the error is an end-of-line error, in which
  // case we will redirect to the generic error page.
  if (GENERIC_ERRORS.hasOwnProperty(errorCode) && !isDev) {
    CI.flag();
    appStore.getActions().ui.toggleIsPageLoading(true);

    if (isGenericErrorPageFeatureEnabled(errorCode)) {
      if (errorCode === 'PAYER_LOGIN_COUNTRY_NOT_ALLOWED') {
        logger.fpti('weasley_payer_login_country_not_allowed_treatment', {
          event_name: 'weasley_payer_login_country_not_allowed_treatment',
        });
      }
      appStore.getActions().ui.setGenericError(errorCode);
      appStore.getActions().ui.toggleIsPageLoading(false);
      return;
    }
    // since we are checking multiple objects
    return GENERIC_ERRORS[errorCode](error, handleDefaultError);
  }

  // Check to see if the error is an unhandled preAccountCreation
  // error, in which case we will redirect to the generic error page.
  if (PRE_ACCOUNT_CREATION_ERRORS.hasOwnProperty(errorCode)) {
    CI.flag();
    appStore.getActions().ui.toggleIsPageLoading(true);
    // since we are checking multiple objects
    return PRE_ACCOUNT_CREATION_ERRORS[errorCode](error);
  }

  // The presence of an access token indicates that the initial account creation
  // in OAS was successful (true for member and guest accounts). It does not necessarily
  // mean that the account created can be used for this (or any future) transaction
  const accessToken = getAccessToken().value || error?.errorData?.accessToken;

  if (!accessToken) {
    // Pre-account creation errors
    logger.warn('weasley_pre_account_creation_error', { error: errorCode });

    // name validation errors also have code as VALIDATION_ERROR, which could
    // conflict with address validation error.
    if (isNameValidationError(error)) {
      return handleNamesValidationError(error);
    }

    if (PAGE_LEVEL_ERRORS.hasOwnProperty(errorCode)) {
      // since we are checking multiple objects
      const contentKey = PAGE_LEVEL_ERRORS[errorCode]();
      return handlePageLevelError({ contentKey, error });
    }
  } else {
    // Post-account creation errors
    const { onboardOption } = appStore.getState().form;
    logger.warn('weasley_post_account_creation_error', {
      error: errorCode,
      onboardOption: onboardOption.value,
    });

    // Check 3DS error - this error is thrown on the client in 3DS flows.
    // Situations where this error might be thrown are when the call to
    // Cardinal Commerce times out or fails.
    if (errorCode === 'WEASLEY_3DS_ERROR' && !isDev) {
      appStore.getActions().ui.toggleIsPageLoading(true);
      return handle3DSError(error);
    }

    // Check post account creation errors. If `errorData` exists, then we use
    // the first code found in that array. If there is no code found then we
    // default to the message as the error key.
    const errorData = error.errorData || error.data;
    const code = getErrorCodeFromErrorData(errorData) || errorCode;

    if (POST_ACCOUNT_CREATION_ERRORS.hasOwnProperty(code)) {
      //@ts-expect-error  it's ok to key based on a string
      // since we are checking multiple objects
      const contentKey = POST_ACCOUNT_CREATION_ERRORS[code](error);
      return handlePostAccountCreationError({
        contentKey,
        error,
        message: code,
      });
    }

    // Check CPA error - CC_AUTHORIZATION_DECLINED.
    // a new object is created with error code and error key from message
    // we go to post account creation error with altered CPA message styling
    if (errorCode.includes(CPA_AUTH_DECLINED_ERROR.API_ERROR_CODE)) {
      const {
        declinedInstruments: [
          {
            // @ts-expect-error we need to have a better default initializer
            card_type: cardType,
            // @ts-expect-error we need to have a better default initializer
            id,
            // @ts-expect-error we need to have a better default initializer
            normalized_processor_response: normalizedProcessorResponse,
          } = {},
        ] = [],
      } = error?.graphQLErrors?.[0]?.data || {};

      const contentKeyFunction =
        // @ts-expect-error it's ok to key based on a string
        // since we are checking multiple objects
        GUEST_CPA_ERRORS[normalizedProcessorResponse] ||
        GUEST_CPA_ERRORS.DEFAULT;
      const contentKey = contentKeyFunction();

      logger.fpti('xopg_auth_fail_fpti', {
        card_brand: cardType,
        declined_reason: normalizedProcessorResponse,
        fi_id: id,
        reason_code: contentKey,
        state_name: 'PRE_AUTH_FI',
        transition_name: 'prepare_declined_fi',
      });
      return handlePostAccountCreationError({
        contentKey,
        error,
        message: code,
      });
    }
  }

  if (error?.graphQLErrors?.[0]?.message === 'WPS_INVENTORY_ERROR') {
    const urlFromError = error?.graphQLErrors?.[0]?.data?.errorData?.soldoutUrl;
    appStore.getActions().ui.toggleIsInventorySoldOutOpen(true);
    appStore.getActions().ui.setSoldOutUrlFromError(urlFromError);
    return;
  }

  // If we hit this point and have been unable to resolve the error
  // we fallback to hermes or xoon, depending on whether or not we
  // have an access token.
  handleDefaultError(error);
};

export const isGenericErrorPageFeatureEnabled = (
  errorCode: string,
): boolean => {
  switch (errorCode) {
    case 'PAYER_LOGIN_COUNTRY_NOT_ALLOWED':
      return features.isActive('isPayerLoginCountryNotAllowed');
    default:
      return false;
  }
};

export { handleApiError };
